import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { VisualOverlay } from '../VisualOverlay';
import { resultsCover, resultsMedia } from '../../data/results';
interface ResultsCoverProps {
  onBegin: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
const SEQUENCE = [resultsMedia.cover, resultsMedia.profiles, resultsMedia.balance, resultsMedia.monitoring];
export function ResultsCover({
  onBegin
}: ResultsCoverProps) {
  const [frame, setFrame] = useState(0);
  const [teaser, setTeaser] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setFrame(f => (f + 1) % SEQUENCE.length), 2600);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (teaser >= resultsCover.teaser.length) return;
    const id = window.setTimeout(() => setTeaser(t => t + 1), teaser === 0 ? 500 : 1300);
    return () => window.clearTimeout(id);
  }, [teaser]);
  return <div className="relative flex h-full flex-col overflow-y-auto bg-white no-scrollbar" data-id="element-645" data-mp="3kfy9hry7s3nx">
      <div className="relative h-[46%] min-h-[300px] shrink-0 overflow-hidden bg-mist-100" data-id="element-646" data-mp="3pe84o2jxv49j">
        <AnimatePresence initial={false} data-id="element-647" data-mp="3ubrg6up952xs">
          <motion.img key={frame} src={SEQUENCE[frame]} alt="" initial={{
          opacity: 0,
          scale: 1.12
        }} animate={{
          opacity: 1,
          scale: 1.02
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 1.1
          },
          scale: {
            duration: 4,
            ease: 'easeOut'
          }
        }} className="absolute inset-0 h-full w-full object-cover" data-id="element-648" data-mp="3uaq2sjkrk4cw" />
        </AnimatePresence>
        <VisualOverlay variant="follicles" accent="#5468D8" data-id="element-649" data-mp="lwzodn4v0cgx" />

        <div className="relative flex items-center justify-between px-6 pt-6 md:pt-11" data-id="element-650" data-mp="ludg1la59sxn">
          <div className="flex items-center gap-2.5" data-id="element-651" data-mp="2ir496624c693">
            <Logo width={24} height={24} data-id="element-652" data-mp="2np5p4i48zpgc" />
            <span className="text-[13px] font-semibold tracking-[0.18em] text-navy-900" data-id="element-653" data-mp="2nq72it8qko18">IVF PULSE</span>
          </div>
          <span className="rounded-full bg-white/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-navy-700 backdrop-blur-md" data-id="element-654" data-mp="2bltcc5ffp8m7">
            Chapter two
          </span>
        </div>

        <div className="absolute inset-x-6 bottom-5 space-y-1.5" aria-live="polite" data-id="element-655" data-mp="2kmhsp6savl29">
          {resultsCover.teaser.slice(0, teaser).map((line, i) => <motion.p key={line} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className={`w-fit rounded-full px-3 py-1.5 text-[13px] font-medium backdrop-blur-md ${i === 1 ? 'bg-navy-900 text-white' : 'bg-white/85 text-navy-900'}`} data-id="element-656" data-mp="2ykpupt471cka" data-mpi={i}>
              {line}
            </motion.p>)}
        </div>
      </div>

      <div className="flex flex-1 flex-col px-6 pb-7 pt-6" data-id="element-657" data-mp="3dcgjb6m57q91">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-iris" data-id="element-658" data-mp="rucsjkvihrd">{resultsCover.eyebrow}</p>
        <motion.h1 initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.2,
        ease
      }} className="mt-3 font-serif text-[36px] leading-[1.02] text-navy-900" data-id="element-659" data-mp="4tfvwmo03sa0">
          {resultsCover.headline}
          <br data-id="element-660" data-mp="3d97wky20znsv" />
          <span className="text-pulse-iris" data-id="element-661" data-mp="3d8psjf94g5ae">{resultsCover.headlineAccent}</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.4,
        ease
      }} className="mt-3 text-[15px] leading-relaxed text-slate-500" data-id="element-662" data-mp="3hzy34em6twp5">
          {resultsCover.subline}
        </motion.p>
        <p className="mt-4 text-[12px] text-slate-400" data-id="element-663" data-mp="2u9lqiqfzzbs8">
          {resultsCover.participants} All percentages shown are illustrative.
        </p>

        <div className="mt-auto pt-6" data-id="element-664" data-mp="2z8f047s5yin4">
          <button type="button" onClick={onBegin} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris focus-visible:ring-offset-2" data-id="element-665" data-mp="6egiozrs68ep">
            Discover the first finding
            <ArrowRightIcon className="h-4 w-4 text-pulse-iris" data-id="element-666" data-mp="3iw4wm80x9d49" />
          </button>
        </div>
      </div>
    </div>;
}